import invApi from './InventoryApi'
import moment from 'moment'
import { ReminderFrequencyUnit } from '../Enums/ReminderFrequencyUnit'

export const getNextReminderDate = (
  startDate,
  frequencyValue,
  frequencyUnit,
  hour
) => {
  // Start from dueDate with specified time
  const initialDate = moment(startDate)
    .hour(hour)
    .minute(0)
    .second(0)
    .millisecond(0)

  const now = moment()
  let reminderDate = initialDate

  // If the initial date is in the past, calculate the next occurrence
  if (initialDate.isBefore(now)) {
    switch (frequencyUnit) {
      case ReminderFrequencyUnit.DAILY:
        // Find next occurrence by adding days until we get a future date
        while (reminderDate.isBefore(now)) {
          reminderDate.add(frequencyValue, 'days')
        }
        break
      case ReminderFrequencyUnit.WEEKLY:
        // Find next occurrence by adding weeks until we get a future date
        while (reminderDate.isBefore(now)) {
          reminderDate.add(frequencyValue * 7, 'days')
        }
        break
      case ReminderFrequencyUnit.MONTHLY:
        // Find next occurrence by adding months until we get a future date
        while (reminderDate.isBefore(now)) {
          reminderDate.add(frequencyValue, 'months')
        }
        break
      default:
        // Default to days if no unit specified
        while (reminderDate.isBefore(now)) {
          reminderDate.add(frequencyValue, 'days')
        }
    }
  }

  return new Date(reminderDate)
}

export const subscribeUserToPush = async (userId, name, email) => {
  const isPushSupported =
    'Notification' in window &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    Notification.requestPermission

  if (!isPushSupported) {
    console.warn('Push notifications not fully supported on this device.')
    return
  }

  try {
    const permission = await Notification.requestPermission()
    if (permission !== 'granted') {
      console.warn('Notification permission denied')
      return
    }

    const registration = await registerAndWaitForControl()

    const existingSubscription =
      await registration.pushManager.getSubscription()
    if (existingSubscription) {
      return
    }

    // If no existing subscription, subscribe
    const vapidPublicKey = await getVAPIDPublicKey()
    const newSubscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
    })
    // Send to backend
    await sendSubscriptionToServer(newSubscription, userId, name, email)
  } catch (error) {
    console.error('Push subscription error:', error)
  }
}

export const unsubscribeUserFromPush = async () => {
  if (!('serviceWorker' in navigator)) {
    console.warn('Service workers are not supported')
    return
  }
  try {
    const registration = await navigator.serviceWorker.ready
    const subscription = await registration.pushManager.getSubscription()
    if (subscription) {
      // Unsubscribe from push notifications
      await subscription.unsubscribe()
    }
  } catch (error) {
    console.error('Error unsubscribing from push notifications:', error)
  }
}

export const registerUser = async (userId, name, email) => {
  try {
    const res = await invApi.post('/notifications/register', {
      id: userId,
      name: name,
      email: email,
    })
    if (!res.data) {
      console.error('Failed to register the user on the server', res)
    }
  } catch (err) {
    console.error('Error registering the user:', err)
  }
}

export const createNotification = async ({
  senderId,
  frequencyUnit,
  frequencyValue,
  timeOption,
  notifiedAdminList,
  title,
  message,
  redirectUrl = '',
  startDate, // 'YYYY-MM-DD'
}) => {
  // Time conversion (assuming timeOption is hours in 24h format as a string)
  const timeOfDay = String(timeOption).padStart(2, '0') + ':00'

  // Recipients mapping (grabbing the `id` from each item)
  const recipients = notifiedAdminList.map((admin) => admin.id)

  // Remove duplicates (just in case)
  const uniqueRecipients = [...new Set(recipients)]

  // Build the payload based on backend validation
  const payload = {
    sender_id: senderId,
    recipients: uniqueRecipients,
    title: title,
    message: message,
    redirect_url: redirectUrl,
    start_date: startDate,
    time_of_day: timeOfDay,
    frequency_type: frequencyUnit,
    frequency_value: frequencyValue,
  }

  try {
    const res = await invApi.post('/notifications', payload)
    if (!res.data) {
      console.error('Failed to create notification on the server')
      const error = await res.json()
      console.error('Server response:', error)
      return null
    }
    return res.data.data.notification_id || null
  } catch (err) {
    console.error('Error creating notification:', err)
    return null
  }
}

export const updateNotification = async ({
  notificationId,
  senderId,
  frequencyUnit,
  frequencyValue,
  timeOption,
  notifiedAdminList,
  title,
  message,
  redirectUrl = '',
  startDate,
}) => {
  const timeOfDay = String(timeOption).padStart(2, '0') + ':00'
  const recipients = [...new Set(notifiedAdminList.map((admin) => admin.id))]

  const payload = {
    sender_id: senderId,
    recipients,
    title,
    message,
    redirect_url: redirectUrl,
    start_date: startDate,
    time_of_day: timeOfDay,
    frequency_type: frequencyUnit,
    frequency_value: frequencyValue,
  }

  const res = await invApi.put(`/notifications/${notificationId}`, payload)
  if (!res.data) {
    console.error('Failed to update notification')
    const error = await res.json()
    console.error('Server response:', error)
    return null
  }
  return res.data.data.notification_id || null
}

export const cancelNotification = async (notificationId) => {
  const res = await invApi.delete(`/notifications/${notificationId}`)

  if (!res.data) {
    console.error('Failed to cancel notification')
    const error = await res.json()
    console.error('Server response:', error)
    return false
  }

  return true
}

// Utility: Convert base64 public key to Uint8Array
const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/\-/g, '+').replace(/_/g, '/')

  const rawData = window.atob(base64)
  const outputArray = new Uint8Array(rawData.length)

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}

const registerAndWaitForControl = async () => {
  await navigator.serviceWorker.register('/service-worker.js')
  // Wait for service worker to be active and controlling the page
  if (!navigator.serviceWorker.controller) {
    // Add timeout to prevent hanging
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(
        () => reject(new Error('Service worker activation timeout')),
        300
      )
    })
    // Wait for either controller change or timeout
    await Promise.race([
      new Promise((resolve) => {
        navigator.serviceWorker.addEventListener('controllerchange', () => {
          resolve()
        })
      }),
      timeoutPromise,
    ])
    window.location.reload()
    return
  }
  const readyRegistration = await navigator.serviceWorker.ready
  return readyRegistration
}

const sendSubscriptionToServer = async (subscription, userId, name, email) => {
  try {
    const res = await invApi.post('/notifications/register', {
      id: userId,
      name: name,
      email: email,
      subscription,
    })
    if (!res.data) {
      console.error('Failed to register subscription on the server')
    }
  } catch (err) {
    console.error('Error sending subscription to server:', err)
  }
}

const getVAPIDPublicKey = async () => {
  try {
    const response = await invApi.get('/notifications/vapid')
    return response.data.vapid
  } catch (error) {
    return null
  }
}
